/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/api.js
 *
 * Purpose: Configures the shared axios instance used for all customer-portal API calls and
 *     defines the resource-grouped API call modules (Orders, Samples, Tests, Auth, etc.).
 *
 * Scope: HTTP client setup and the full set of REST call wrappers backing the portal's data
 *     layer.
 *
 * Tags: customer-portal
 *
 * Overview: Creates an axios instance pointed at REACT_APP_BASE_URL (falling back to
 *     "/portal/api"), attaches a progress bar via axios-progress-bar/NProgress, and sets a
 *     15-minute timeout plus a local-timezone header. A response interceptor maps HTTP
 *     errors to user-facing strings and fires a NotificationManager toast (skipping logout/
 *     refresh-token calls and 401/404 responses), while a request interceptor injects
 *     customer/division ID headers from localStorage/sessionStorage. Exports one object per
 *     resource (Auth, Orders, Samples, Tests, Attachments, Assets, Assays, LabelTemplates,
 *     Labels, PrintDocTemplates, PrintDocs, Invoices, Customers, Projects, Sources, Contacts,
 *     Comments, Metrics) whose methods call the axios instance, often first checking a stub
 *     module (e.g. `AuthStub`, `OrderStub`) for mocked responses. Also exports a generic
 *     `useApi` hook for simple GET/method fetches with loading state.
 *
 * Dependencies: shared/stub/* modules used as short-circuit mocks ahead of live API calls
 *
 * Exports: axiosinstance, useApi, Fields, Auth, Orders, Samples, Tests, Attachments, Assets,
 *     Assays, LabelTemplates, Labels, PrintDocTemplates, PrintDocs, Invoices, Customers,
 *     Projects, Sources, Contacts, Comments, Metrics
 */

import axios from "axios";
import { loadProgressBar } from "axios-progress-bar";
import "axios-progress-bar/dist/nprogress.css";
import jstz from "jstz";
import NProgress from "nprogress";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { QueryKeys } from "shared/constants/entityTypes";
import { AssayStub } from "shared/stub/assays";
import { AttachmentStub } from "shared/stub/attachments";
import { AuthStub } from "shared/stub/auth";
import { OrderStub } from "shared/stub/orders";
import { SampleStub } from "shared/stub/samples";
import { objectToQueryParams } from "./misc";

const baseURL = process.env.REACT_APP_BASE_URL || "/portal/api";

export const axiosinstance = axios.create({
  baseURL,
  timeout: 60000*15,
  withCredentials: true,
  headers: {
    "content-type": "application/json",
    "X-Local-Timezone": jstz.determine().name(),
  },
});

loadProgressBar(NProgress, axiosinstance);

// If we receive a `MirageValidationError` when logging out or refreshing access token,
// chances are it's a tokenless request, so those two paths never toast.
const TOKENLESS_PATHS = ['logout', 'refresh_token'];

// Lifted out of the response interceptor below, which the CCN gate flagged: the branch order,
// every message string and the `.some` path comparison are carried over unchanged. Three of
// these messages are computed but never shown: the only reader is the toast call below, and
// that is guarded on a response whose status is neither 401 nor 404 — so the 404 string is
// unreachable there by construction, and a timeout or network failure carries no response at
// all. Changing any of those three wordings has no visible effect; they are kept because
// removing them is a behaviour question, not a boy-scout one.
const errorMessageFor = (error) => {
  if (error.response) {
    if (error.response.status === 404) return 'Resource Not Found.';
    if (error.response.status === 403) {
      return 'Forbidden: Insufficient permissions.';
    }
    return error.response.data && error.response.data.message
      ? error.response.data.message
      : 'Unknown Error';
  }
  if (error.code === 'ECONNABORTED') {
    return 'Timeout exceeded ' + error.request.timeout + 'ms';
  }
  if (error.isAxiosError) return 'Network error';
  return 'Unknown Error';
};

// Same lift, same rationale. A request may opt out of the toast via `skipErrorNotification`
// when specific failures are expected and handled by the caller. Pass an array of statuses to
// silence only those (e.g. [403, 404] for a prefetch whose permission-denied / integration-off
// responses just mean "nothing to offer"); pass `true` to silence all. Unlisted statuses
// (e.g. 5xx) still toast, so genuine breakage is never hidden. The status read is optional-
// chained rather than leaning on the call site's guard order: a responseless error reaching
// here with an array-valued opt-out (which `shared/hooks/api/dropboxSign.js` passes) would
// otherwise throw out of the rejection handler instead of rejecting cleanly.
const shouldNotifyFor = (error) => {
  const skip = error.config?.skipErrorNotification;
  const optedOut = Array.isArray(skip)
    ? skip.includes(error.response?.status)
    : Boolean(skip);
  const lastSegment = (error.config?.url || '').split('/').at(-1);
  return !optedOut && !TOKENLESS_PATHS.some((path) => path === lastSegment);
};

axiosinstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const errorString = errorMessageFor(error);

    if (
      error.response &&
      error.response.status !== 401 &&
      error.response.status !== 404 &&
      shouldNotifyFor(error)
    ) {
      NotificationManager.error(errorString, 'Error', 3000);
    }

    return Promise.reject({
      ...error.response,
      isAxiosError: error.isAxiosError,
    });
  },
);
axiosinstance.interceptors.request.use((config) => {
  config.headers["X-Customer-ID"] = localStorage.getItem("customerId");
  config.headers["X-Division-ID"] = sessionStorage.getItem("divisionId");
  return config;
});

export const useApi = (method, url) => {
  const [result, setResult] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    axiosinstance[method.toLowerCase()](url).then((r) => {
      if (!cancelled) {
        setResult(r.data);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [method, url]);

  return [result, loading];
};

export const Fields = {
  all: () => axiosinstance.get("/v1/fieldconfig"),
  byDivision: () => axiosinstance.get('/v1/fields'),
};

export const Auth = {
  current: () => AuthStub.current() || axiosinstance.get(`/v1/userinfo`),
  login: (email, password) =>
    AuthStub.login() ||
    axiosinstance.post(`/v1/login`, JSON.stringify({ email, password })),
  samlLogin: (authCode) =>
    axiosinstance.post(`/v1/login/sso/saml`, { qb_auth: authCode }),
  validateMFA: (email, password) => 
    AuthStub.validateMFA() || 
    axiosinstance.post(`/v1/mfa/validate`, JSON.stringify({ email, password })),
  logout: () =>
    AuthStub.logout() ||
    axiosinstance.post(`/v1/logout`, {
      csrf_token: localStorage.getItem("csrfToken"),
    }),
  forgotPwd: (email) =>
    AuthStub.forgotPwd() ||
    axiosinstance.post(`/v1/forgotpwd`, JSON.stringify({ email })),
  forgotPwdValidate: (email, code) =>
    AuthStub.forgotPwdValidate() ||
    axiosinstance.post(
      `/v1/forgotpwd/validate`,
      JSON.stringify({ email, code })
    ),
  resetPwd: (data) =>
    AuthStub.resetPwd() ||
    axiosinstance.post(`/v1/password/reset`, JSON.stringify(data)),
  refreshToken: () =>
    axiosinstance.post(`/v1/refresh_token`, {
      csrf_token: localStorage.getItem("csrfToken"),
    }),
  signUp: (signUpData) => 
    AuthStub.signUp() || axiosinstance.post(`/v1/signup`, JSON.stringify(signUpData)),
  divisionData: () => axiosinstance.get(`/v1/divisions`),
};

export const Orders = {
  create: (order) => axiosinstance.post(`/v1/order`, order),
  get: (id) => OrderStub.get(id) || axiosinstance.get(`/v1/order/${id}`),
  update: (order, checkRequiredFields = true) => {
    return axiosinstance.patch(
      `/v1/order/${order.id}?check_required_fields=${checkRequiredFields}`,
      order
    )
  },
  delete: (id) =>
    OrderStub.delete(id) || axiosinstance.delete(`/v1/order/${id}`),
  all: (page = 1, pageSize = 20) =>
    OrderStub.all(page, pageSize) ||
    axiosinstance.get(`/v1/orders?page_num=${page}&page_size=${pageSize}`),
};

export const Samples = {
  create: (sample) =>
    SampleStub.create(sample) || axiosinstance.post(`/v1/sample`, sample),
  get: (id) => SampleStub.get(id) || axiosinstance.get(`/v1/sample/${id}`),
  getByOrderId: (orderId) =>
    SampleStub.getByOrderId(orderId) ||
    axiosinstance.get(`/v1/order/${orderId}/samples`),
  update: (sample) =>
    SampleStub.update(sample) ||
    axiosinstance.patch(`/v1/sample/${sample.id}`, sample),
  delete: (id) =>
    SampleStub.delete(id) || axiosinstance.delete(`/v1/sample/${id}`),
  all: (page = 1, pageSize = 20) =>
    SampleStub.all(page, pageSize) ||
    axiosinstance.get(`/v1/samples?page_num=${page}&page_size=${pageSize}`),
  clone: (id) =>
    SampleStub.clone(id) || axiosinstance.post(`/v1/sample/${id}/clone`),
};

export const Tests = {
  getByOrderId: (orderId) => axiosinstance.get(`/v1/order/${orderId}/${QueryKeys.TEST}`),
  createMulti: (tests) => axiosinstance.post(`/v1/${QueryKeys.TEST}`, tests),
  upsertMulti: (tests) => axiosinstance.post(`/v1/${QueryKeys.TEST}/save`, tests),
  deleteMulti: (tests) => axiosinstance.post(`/v1/${QueryKeys.TEST}/delete`, tests),
};

export const Attachments = {
  save: (dataType, attachment) =>
    AttachmentStub.save(dataType, attachment) ||
    axiosinstance.post(`/v1/${dataType}/attachment/save`, attachment),
  get: (dataType, params) =>
    AttachmentStub.get(dataType, params) ||
    axiosinstance.get(
      `/v1/${dataType}/attachments?${objectToQueryParams(params)}`
    ),
  delete: (entType, attachmentId) =>
    axiosinstance.delete(`/v1/${entType}/attachment/${attachmentId}`),
};

export const Assets = {
  checkUpload: (assetId) => axiosinstance.get(`/v1/asset/${assetId}/uploaded`),
  download: (assetId) => axiosinstance.get(`/v1/asset/${assetId}/download`),
};

export const Assays = {
  get: (params) =>
    AssayStub.get(params) ||
    axiosinstance.get(`/v1/assays?${objectToQueryParams(params)}`),
};

export const LabelTemplates = {
  getByType: (level) =>
    axiosinstance.get(`/v1/template/labels/${level.toLowerCase()}`),
  get: () => axiosinstance.get(`/v1/template/labels`),
};
export const Labels = {
  generate: (dataType, label_id, order_id) =>
    axiosinstance.post(`/v1/template/labels/${dataType}/print`, {
      label_id,
      order_id,
    }),
};

export const PrintDocTemplates = {
  getByType: (level) =>
    axiosinstance.get(`/v1/template/printdocs/${level.toLowerCase()}`),
  get: () => axiosinstance.get(`/v1/template/printdocs`),
};

export const PrintDocs = {
  get: (id) => axiosinstance.get(`/v1/printdoc/${id}`),
  view: (id) => axiosinstance.get(`/v1/printdoc/${id}/view`),
  generate: (level, ent_id, template_id) =>
    axiosinstance.post(`/v1/printdoc/${level.toLowerCase()}/generate`, {
      ent_id,
      template_id,
    }),
  getEntityPrintDocByTemplateId: (entity, entityId, templateId) =>
    axiosinstance.get(
      `/v1/${entity}/${entityId}/printdocs?template_id=${templateId}`
    ),
};

export const Invoices = {
  billing: () => axiosinstance.get(`/v1/billing`),
};

export const Customers = {
  get: (id) => axiosinstance.get(`/v1/${QueryKeys.CUSTOMER}/${id}`),
  update: (customer) => axiosinstance.patch(`/v1/${QueryKeys.CUSTOMER}/${customer.id}`, customer),
};

export const Projects = {
  create: (project) => axiosinstance.post(`/v1/projects`, project),
};

function postFile(url, file) {
  const fd = new FormData();
  fd.append('file', file);
  return axiosinstance.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
}

export const Sources = {
  create: (source) => axiosinstance.post(`/v1/${QueryKeys.SOURCE}`, source),
  update: (source) => axiosinstance.patch(`/v1/${QueryKeys.SOURCE}/${source.id}`, source),
  import: (file) => postFile(`/v1/${QueryKeys.SOURCE}/import/upsert`, file),
  importPreview: (file) => postFile(`/v1/${QueryKeys.SOURCE}/import/preview`, file),
  importTemplate: (entIds = []) => {
    const params = new URLSearchParams();
    entIds.forEach((id) => params.append('ent_ids', id));
    return axiosinstance.get(`/v1/${QueryKeys.SOURCE}/import/template/upsert`, {
      params,
      responseType: 'blob',
    });
  },
};

export const Contacts = {
  save: (customerId, contacts) =>
    axiosinstance.post(
      `/v1/${QueryKeys.CUSTOMER}/${customerId}/${QueryKeys.CONTACT}`,
      contacts
    ),
  invite: (customerId, contactId, payload) =>
    axiosinstance.post(
      `/v1/${QueryKeys.CUSTOMER}/${customerId}/${QueryKeys.CONTACT}/${contactId}/invite`,
      payload
    ),
};

export const Comments = {
  create: (orderId, comment) =>
    axiosinstance.post(`/v1/order/${orderId}/comment`, comment),
  sub: (orderId) => axiosinstance.post(`/v1/order/${orderId}/follow/comments`),
  unsub: (orderId) => axiosinstance.delete( `/v1/order/${orderId}/follow/comments`),
};

export const Metrics = {
  get: () => axiosinstance.get(`/v1/dashboard/metrics`),
};
